/**
 * Custom hook for the P2P-compliant click-and-hold send button.
 *
 * Usage:
 *   const { bindHold, isHolding, progress, isReady } = useClickAndHold({
 *     threshold: 200,
 *     onSend: () => sendMessage(),
 *   });
 *   <button {...bindHold}>Send</button>
 */
import { useState, useRef, useCallback } from "react";

export default function useClickAndHold({ threshold = 200, onSend, cooldown = 500 }) {
  const [isHolding, setIsHolding] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isReady, setIsReady] = useState(false);
  const [isCoolingDown, setIsCoolingDown] = useState(false);

  const startTimeRef = useRef(null);
  const animationRef = useRef(null);
  const inputMethodRef = useRef("mouse");

  const startHold = useCallback((method = "mouse") => {
    if (isCoolingDown) return;

    inputMethodRef.current = method;
    startTimeRef.current = Date.now();
    setIsHolding(true);
    setIsReady(false);

    const animate = () => {
      const elapsed = Date.now() - startTimeRef.current;
      const pct = Math.min(elapsed / threshold, 1);
      setProgress(pct);

      if (pct >= 1) {
        setIsReady(true);
      }

      if (elapsed < threshold * 1.2) {
        animationRef.current = requestAnimationFrame(animate);
      }
    };

    animationRef.current = requestAnimationFrame(animate);
  }, [threshold, isCoolingDown]);

  const endHold = useCallback(() => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    const holdDuration = startTimeRef.current ? Date.now() - startTimeRef.current : 0;

    if (holdDuration >= threshold && onSend) {
      onSend({
        holdDurationMs: holdDuration,
        inputMethod: inputMethodRef.current,
      });

      // Cooldown
      setIsCoolingDown(true);
      setTimeout(() => setIsCoolingDown(false), cooldown);
    }

    setIsHolding(false);
    setProgress(0);
    setIsReady(false);
    startTimeRef.current = null;
  }, [threshold, onSend, cooldown]);

  const cancelHold = useCallback(() => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    setIsHolding(false);
    setProgress(0);
    setIsReady(false);
    startTimeRef.current = null;
  }, []);

  const bindHold = {
    onMouseDown: () => startHold("mouse"),
    onMouseUp: endHold,
    onMouseLeave: cancelHold,
    onKeyDown: (e) => {
      if (e.code === "Space" && !e.repeat) {
        e.preventDefault();
        startHold("keyboard");
      }
    },
    onKeyUp: (e) => {
      if (e.code === "Space") {
        e.preventDefault();
        endHold();
      }
    },
  };

  return { bindHold, isHolding, progress, isReady, isCoolingDown };
}
